import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote";
import { components } from "components/mdx/privacy-components";
import { Section, sectionSpacingY } from "components/section";
import { Link } from "ui/link";
import { type VersionHistoryEntry } from "data/legal/types";
import { OldVersionAlert } from "./old-version-alert";

type LegalPageProps = {
  /**
   * Page version identifier
   * @default "current"
   */
  pageVersion?: string;
  title: string;
  lastUpdatedDate: string;
  mdxSource: MDXRemoteSerializeResult;
  currentVersionSlug: string;
  versionHistory?: VersionHistoryEntry[];
};

export function LegalPage({
  pageVersion = "current",
  title,
  lastUpdatedDate,
  mdxSource,
  currentVersionSlug,
  versionHistory,
}: LegalPageProps) {
  const isCurrentVersion = pageVersion === "current";

  return (
    <Section fontFamily="inter" maxW="3xl" mx="auto" my={0} mt={{ base: "small", md: "huge" }} pb={sectionSpacingY}>
      <Flex
        direction="column"
        fontFamily="inter"
        gap="big"
        mb={{ base: "bigger", md: "biggest" }}
        textAlign="center"
        justify="center"
      >
        <Text as="h1" textStyle="article.h1">
          {title}
        </Text>

        <Text textStyle="caption1" color="brand.textSecondary.light" textAlign="center">
          Last updated {lastUpdatedDate}
        </Text>

        {!isCurrentVersion && <OldVersionAlert title={title} currentVersionSlug={currentVersionSlug} />}
      </Flex>

      <MDXRemote {...mdxSource} components={components} />

      {
        /**
         * Only show the previous versions on the current page for now, since there will only ever be one previous version
         * @TODO  Fast follow fix this to support multiple previous versions on any page, current or not.
         */
        isCurrentVersion && versionHistory && versionHistory.length > 0 && (
          <Box fontFamily="inter">
            <Text as="h2" textStyle="article.h3" mb="small">
              Previous Versions
            </Text>
            <Text textStyle="article.p">
              The previous version of Hightouch&apos;s {title} can be found{" "}
              <Link variant="inlineDark" href={`${versionHistory[0].path}`}>
                here
              </Link>
              .
            </Text>
            {/** @TODO @taylor Add a toggle-able section to view ALL previous versions */}
          </Box>
        )
      }

      {!isCurrentVersion && <OldVersionAlert title={title} currentVersionSlug={currentVersionSlug} mt="big" />}
    </Section>
  );
}
